import { Link } from "@tanstack/react-router";
import {
  LuCircleAlert,
  LuClipboardCheck,
  LuTriangleAlert,
  LuUserRoundX,
  LuUsersRound,
} from "react-icons/lu";
import { useQuery } from "@/lib/convex";
import type { ReactNode } from "react";
import {
  Accordion,
  Badge,
  Box,
  Card,
  Code,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from "@chakra-ui/react";
import { api } from "@convex/_generated/api";
import { PageHeader } from "../components/PageHeader";
import { StatCard } from "../components/StatCard";
import { CopyableTable } from "../components/tableCopy";

const EXCLUSION_LABELS: Record<string, string> = {
  ccriCluster: "CCRI cluster agreement",
  aithyra: "Aithyra (sister institute)",
  externalCollaborator: "University collaborators",
  techConsultant: "Technical consultants",
  teamsOnly: "Microsoft Teams access only",
  pendingDeactivation: "Awaiting deactivation",
};

const SOURCE_LABELS: Record<string, string> = {
  directory: "People directory",
  comeAndGo: "HR come-and-go list",
  hr: "HR",
  tto: "TTO",
  programme: "Programme manager",
  curation: "Entered by hand",
};

const BASIS_LABELS: Record<string, string> = {
  employment: "CeMM employment",
  hostedExternal: "Hosted, externally employed",
  collaborationWorking: "Collaboration — working member",
  collaborationVisitor: "Collaboration — short-term visitor",
  collaborationWrapUp: "Collaboration — wrap-up",
};

/**
 * One reconciliation check, collapsed to its count until opened.
 *
 * Collapsed by default because the page is a list of questions, and most of them
 * usually have no answer; a reader wants to see which need attention before
 * reading any of them. A section with nothing to resolve does not open at all.
 */
function Section({
  value,
  title,
  description,
  count,
  children,
}: {
  value: string;
  title: string;
  description: string;
  count: number;
  children: ReactNode;
}) {
  const resolved = count === 0;
  return (
    <Accordion.Item value={value} disabled={resolved}>
      {/* Chakra styles the disabled cursor but not the enabled one, so an open
          section gives no hint that the header is what you click. */}
      <Accordion.ItemTrigger px={5} py={4} cursor={resolved ? "not-allowed" : "pointer"}>
        <HStack flex="1" justify="space-between" align="flex-start" gap={4} textAlign="left">
          <Box>
            <Heading size="sm">{title}</Heading>
            <Text mt={1} fontSize="sm" color="fg.muted">
              {description}
            </Text>
          </Box>
          <HStack gap={3}>
            <Badge
              colorPalette={resolved ? "green" : "orange"}
              variant={resolved ? "subtle" : "solid"}
              size="lg"
            >
              {resolved ? "clear" : count}
            </Badge>
            {resolved ? null : <Accordion.ItemIndicator />}
          </HStack>
        </HStack>
      </Accordion.ItemTrigger>
      <Accordion.ItemContent>
        <Accordion.ItemBody px={5} pb={5}>
          {children}
        </Accordion.ItemBody>
      </Accordion.ItemContent>
    </Accordion.Item>
  );
}

function PersonCell({ name, shortName }: { name: string; shortName: string | null }) {
  if (!shortName) {
    return (
      <Stack gap={0}>
        <Text fontWeight="medium">{name}</Text>
        <Text fontSize="xs" color="fg.muted">
          no account
        </Text>
      </Stack>
    );
  }
  return (
    <Stack gap={0}>
      <Link to="/cemmies/$shortName" params={{ shortName }}>
        <Text fontWeight="medium" _hover={{ textDecoration: "underline" }}>
          {name}
        </Text>
      </Link>
      <Text fontSize="xs" color="fg.muted">
        {shortName}
      </Text>
    </Stack>
  );
}

function ReportTable({ headers, children }: { headers: string[]; children: ReactNode }) {
  return (
    <Box borderWidth="1px" borderColor="border">
      <CopyableTable>
        <Table.Root size="sm" variant="line">
          <Table.Header>
            <Table.Row>
              {headers.map((header) => (
                <Table.ColumnHeader key={header}>{header}</Table.ColumnHeader>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>{children}</Table.Body>
        </Table.Root>
      </CopyableTable>
    </Box>
  );
}

export function ReconciliationPage() {
  const report = useQuery(api.admin.reconciliation, {});

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title="Reconciliation"
        description="Where the organisational record and the directory feeds disagree, and what is waiting on a decision."
        breadcrumbs={[{ label: "CeMMbase", to: "/" }, { label: "Administration", to: "/admin" }, { label: "Reconciliation" }]}
      />

      <Box px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        {report === undefined ? (
          <Text fontSize="sm" color="fg.muted">
            Loading…
          </Text>
        ) : (
          <Stack gap={6}>
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap={4}>
              <StatCard
                label="CeMMies"
                value={String(report.summary.cemmies)}
                detail="Deduplicated by person, counting bases only"
                icon={LuUsersRound}
                tone="primary"
              />
              <StatCard
                label="Alumni (active)"
                value={String(report.summary.alumniActive)}
                detail="Still on a collaboration agreement, outside the headcount"
                icon={LuClipboardCheck}
                tone="secondary"
              />
              <StatCard
                label="Accounts"
                value={String(report.summary.accountsCurrent)}
                detail={`${report.summary.external} hold no affiliation`}
                icon={LuUserRoundX}
                tone="info"
              />
              <StatCard
                label="Estimated starts"
                value={String(report.summary.estimatedStarts)}
                detail={`of ${report.summary.affiliationsTotal} affiliations, pending HR dates`}
                icon={LuTriangleAlert}
                tone="accent"
              />
            </SimpleGrid>

            <Card.Root borderWidth="1px" borderColor="border">
              <Accordion.Root multiple collapsible variant="plain">
                <Section
                  value="needsPrimary"
                  title="Needs a primary group"
                  description="Several current affiliations and none chosen as primary. Supervisor resolution returns nothing for these people, so their activities cannot target a reviewer."
                  count={report.needsPrimary.length}
                >
                  <ReportTable headers={["Person", "Units", "Set primary"]}>
                    {report.needsPrimary.map((row) => (
                      <Table.Row key={row.personId}>
                        <Table.Cell>
                          <PersonCell name={row.name} shortName={row.shortName} />
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="sm" color="fg.muted">
                            {row.units.join(" · ")}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          {row.shortName ? (
                            <Code fontSize="xs">
                              curate.ts set-primary --person {row.shortName} --unit{" "}
                              {row.units[0]}
                            </Code>
                          ) : null}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </ReportTable>
                </Section>

                <Section
                  value="affiliationWithoutMembership"
                  title="Affiliation with no group membership"
                  description="A current affiliation whose unit still has an Entra group, but the person is not a member of it. Someone has left a group they are still recorded in, or IT has not caught up."
                  count={report.affiliationWithoutMembership.length}
                >
                  <ReportTable headers={["Person", "Unit", "Basis"]}>
                    {report.affiliationWithoutMembership.map((row) => (
                      <Table.Row key={`${row.personId}-${row.unit}`}>
                        <Table.Cell>
                          <PersonCell name={row.name} shortName={row.shortName} />
                        </Table.Cell>
                        <Table.Cell>{row.unit}</Table.Cell>
                        <Table.Cell>
                          <Text fontSize="xs" color="fg.muted">
                            {BASIS_LABELS[row.basis] ?? row.basis}
                          </Text>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </ReportTable>
                </Section>

                <Section
                  value="accountsWithoutAffiliation"
                  title="Account with no affiliation, and no known reason"
                  description="Someone IT provisioned whose organisational record never arrived. Accounts with a documented reason are counted separately — this list is only the unexplained ones."
                  count={report.accountsWithoutAffiliation.length}
                >
                  <ReportTable headers={["Person", "Short name", "Account"]}>
                    {report.accountsWithoutAffiliation.map((row) => (
                      <Table.Row key={row.shortName}>
                        <Table.Cell>
                          <Text fontWeight="medium">{row.name}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="sm" color="fg.muted">
                            {row.shortName}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge
                            size="sm"
                            colorPalette={row.accountEnabled ? "green" : "gray"}
                            variant="subtle"
                          >
                            {row.accountEnabled ? "enabled" : "disabled"}
                          </Badge>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </ReportTable>
                </Section>

                <Section
                  value="unitsWithoutHead"
                  title="Open unit with no head"
                  description="Nobody is recorded as head, so activities for this unit's people have no supervisor to route to. Sub-units are not listed: they are informational only and resolve to their parent."
                  count={report.unitsWithoutHead.length}
                >
                  <ReportTable headers={["Unit", "Add a head"]}>
                    {report.unitsWithoutHead.map((row) => (
                      <Table.Row key={row.slug}>
                        <Table.Cell>
                          <Link to="/groups/$shortName" params={{ shortName: row.slug }}>
                            <Text fontWeight="medium" _hover={{ textDecoration: "underline" }}>
                              {row.unit}
                            </Text>
                          </Link>
                        </Table.Cell>
                        <Table.Cell>
                          <Code fontSize="xs">
                            curate.ts add-affiliation --person &lt;short&gt; --unit {row.slug} --head
                          </Code>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </ReportTable>
                </Section>

                <Section
                  value="accountsExplained"
                  title="Accounts explained"
                  description="Accounts that hold no affiliation for a documented reason — a lab using Teams, cluster access, a sister institute, consultancy. Every one is legitimate, which is why they sit here rather than among the checks above."
                  count={Object.values(report.accountsExplained).reduce(
                    (total, count) => total + count,
                    0,
                  )}
                >
                  <Stack gap={4}>
                    <HStack flexWrap="wrap" gap={2}>
                      {Object.entries(report.accountsExplained).map(([reason, count]) => (
                        <Badge key={reason} variant="subtle" size="sm">
                          {EXCLUSION_LABELS[reason] ?? reason}: {count}
                        </Badge>
                      ))}
                    </HStack>
                    <ReportTable headers={["Person", "Short name", "Reason", "Account"]}>
                      {report.accountsExplainedList.map((row) => (
                        <Table.Row key={row.shortName}>
                          <Table.Cell>
                            <Text fontWeight="medium">{row.name}</Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Text fontSize="sm" color="fg.muted">{row.shortName}</Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Text fontSize="sm">{EXCLUSION_LABELS[row.reason] ?? row.reason}</Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Badge
                              size="sm"
                              colorPalette={row.accountEnabled ? "green" : "gray"}
                              variant="subtle"
                            >
                              {row.accountEnabled ? "enabled" : "disabled"}
                            </Badge>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </ReportTable>
                  </Stack>
                </Section>

                <Section
                  value="bySource"
                  title="Where affiliations came from"
                  description="Which feed asserted each affiliation, and how much of the record is still provisional."
                  count={report.summary.affiliationsTotal}
                >
                  <Stack gap={4}>
                    <Text fontSize="sm" color="fg.muted">
                      <Icon as={LuCircleAlert} boxSize={3.5} mr={1} verticalAlign="text-bottom" />
                      {report.summary.estimatedStarts} of {report.summary.affiliationsTotal} still
                      carry an estimated start date. Real dates arrive with HR's come-and-go lists.
                    </Text>
                    <HStack flexWrap="wrap" gap={2}>
                      {Object.entries(report.summary.bySource).map(([source, count]) => (
                        <Badge key={source} variant="subtle" size="sm">
                          {SOURCE_LABELS[source] ?? source}: {count}
                        </Badge>
                      ))}
                    </HStack>
                    <ReportTable headers={["Person", "Unit", "Source", "Start", "Status"]}>
                      {report.affiliationsList.map((row, index) => (
                        <Table.Row key={`${row.shortName ?? row.name}-${row.unit}-${index}`}>
                          <Table.Cell>
                            <PersonCell name={row.name} shortName={row.shortName} />
                          </Table.Cell>
                          <Table.Cell>
                            <Text fontSize="sm">{row.unit}</Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Text fontSize="sm" color="fg.muted">
                              {SOURCE_LABELS[row.source] ?? row.source}
                            </Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Text fontSize="sm" color="fg.muted">
                              {/* An estimate reads as one, so nobody mistakes the
                                  import date for a fact. */}
                              {row.startDateIsEstimated ? `about ${row.startDate}` : row.startDate}
                            </Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Badge
                              size="sm"
                              colorPalette={row.isCurrent ? "green" : "gray"}
                              variant="subtle"
                            >
                              {row.isCurrent ? "current" : "ended"}
                            </Badge>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </ReportTable>
                  </Stack>
                </Section>
              </Accordion.Root>
            </Card.Root>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
